import React, { useEffect, useState } from "react";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@nextui-org/react";
import LoadingPage from "../../components/Loader/LoadingPage";
import TableSkeleton from "../../components/Skeleten/TableSkeleton";
import NullData from "../../components/ErrorPage/NullData";
import { useNavigate } from "react-router-dom";
import {
  getDataWithFilterUrl,
  StringLimit,
} from "../../controllers/APIController";

/**
 * SingleFrameworkTable Component
 * Displays a table of control metrics for a specific framework
 * Fetches and displays metric data with pagination support
 * @param {Object} props - Component props
 * @param {Object} props.data - Framework data object
 * @param {string} props.frameworkName - Name of the framework
 * @returns {JSX.Element} Framework metrics table
 */
const SingleFrameworkTable = ({ data, frameworkName }) => {
  // State for loading and data management
  const [isLoading, setIsLoading] = useState(true);
  const [controlMetrics, setControlMetrics] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Navigation hook
  const navigate = useNavigate();

  /**
   * Handle navigation to metric detail page
   * Navigates to the metric with framework context
   * @param {Object} item - Metric item to navigate to
   */
  const handleNavigate = (item) => {
    navigate(`${item?.id}?framework=${frameworkName}`);
  };

  /**
   * Fetch control metrics data for the framework
   * Retrieves metric details for each control metric ID
   */
  useEffect(() => {
    const metricIdList = data?.attributes?.ControlMetrics?.id || [];
    const metricsList = [];
    
    const fetchMetrics = metricIdList?.map(async (id) => {
      const url = `filters[id][$eqi]=${+id}`;
      const response = await getDataWithFilterUrl("metrics", url);
      if (response?.data[0]) metricsList.push(response?.data[0]);
    });

    Promise.all(fetchMetrics).then((data) => {
      // Sort metrics by ID for consistent ordering
      metricsList.sort((a, b) => a.id - b.id);
      setControlMetrics(metricsList);
      setIsLoading(false);
    });
  }, [data]);

  // Pagination configuration
  const rowsPerPage = 15;
  const totalPages = Math.ceil(controlMetrics?.length / rowsPerPage);

  return (
    <div className="my-8">
      <div className="mt-8">
        <LoadingPage isLoading={isLoading} alterElement={<TableSkeleton />}>
          {/* Metrics Table */}
          <Table
            aria-label="Framework control metrics table"
            removeWrapper
            isHeaderSticky
            selectionMode="single"
          >
            {/* Table Header */}
            <TableHeader>
              <TableColumn>Framework's Metric Name</TableColumn>
              <TableColumn>Category</TableColumn>
              <TableColumn>Description</TableColumn>
            </TableHeader>
            
            {/* Table Body */}
            <TableBody emptyContent={<NullData />}>
              {controlMetrics?.map((item, index) => {
                // Pagination logic - only show items for current page
                const lowerPageLimit = (currentPage - 1) * rowsPerPage;
                const upperPageLimit = currentPage * rowsPerPage;
                if (index < lowerPageLimit || index >= upperPageLimit)
                  return null;
                
                return (
                  <TableRow
                    key={item?.id}
                    onClick={() => handleNavigate(item)}
                    className="cursor-pointer"
                  >
                    {/* Metric Name - prioritize framework-specific name */}
                    <TableCell>
                      {item?.attributes?.NameList?.[frameworkName] ||
                        item?.attributes?.NameList?.Generic ||
                        item?.attributes?.Name}
                    </TableCell>
                    
                    {/* Metric Category */}
                    <TableCell>{item?.attributes?.Category}</TableCell>
                    
                    {/* Metric Description */}
                    <TableCell>
                      {StringLimit(item?.attributes?.Description || "--", 70)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          {controlMetrics?.length > 0 && (
            <div className="flex items-center justify-center mt-4">
              <Pagination
                isCompact
                showShadow
                showControls
                color="secondary"
                page={currentPage}
                total={totalPages}
                onChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </LoadingPage>
      </div>
    </div>
  );
};

export default SingleFrameworkTable;
